
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ListingStatus } from "@/lib/types/adminTypes";
import { Label } from "@/components/ui/label";

interface StatusChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  status: ListingStatus | null;
}

export function StatusChangeDialog({
  open,
  onOpenChange,
  onConfirm,
  status
}: StatusChangeDialogProps) {
  const [notes, setNotes] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<ListingStatus | undefined>(status || undefined);

  const getStatusTitle = (status?: ListingStatus | null) => {
    switch(status) {
      case "published": return "Publish Listing";
      case "draft": return "Move to Draft";
      case "archived": return "Archive Listing";
      default: return "Change Status";
    }
  };

  const getStatusDescription = (status?: ListingStatus | null) => {
    switch(status) {
      case "published": 
        return "This listing will be visible to all users on the site.";
      case "draft": 
        return "This listing will be saved as a draft and not visible to users.";
      case "archived": 
        return "This listing will be archived and not shown in any searches.";
      default: 
        return "Select a new status for this listing.";
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{getStatusTitle(selectedStatus || status)}</DialogTitle>
          <DialogDescription>
            {getStatusDescription(selectedStatus || status)}
          </DialogDescription>
        </DialogHeader>
        
        {!status && (
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">Status</Label>
              <Select 
                value={selectedStatus} 
                onValueChange={(value) => setSelectedStatus(value as ListingStatus)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="notes" className="text-right">Notes</Label>
            <Textarea 
              id="notes" 
              placeholder="Add optional notes about this status change" 
              className="col-span-3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Confirm Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
